// piano-frontend/src/components/DateImpactPreview.js
// 관리자 설정 변경 dry-run 미리보기 패널.
// 날짜(+가상 변경)를 받아 그 날짜의 유효 상태와, 변경 시 영향을 받는 예약을 보여준다.
// 표시 전용 — 저장 동작에는 관여하지 않는다.
import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, CircularProgress, Stack, Chip, Divider
} from "@mui/material";
import {
  CheckCircle, Warning as WarningIcon, Info as InfoIcon, EventBusy
} from "@mui/icons-material";
import api from "../api/axios";
import { useDarkMode } from "../App";

const MAX_CHIPS = 8;

function ChipList({ items, color, getLabel }) {
  if (!items || items.length === 0) return null;
  const shown = items.slice(0, MAX_CHIPS);
  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" mt={0.5}>
      {shown.map((item, idx) => (
        <Chip
          key={item.id ?? idx}
          size="small"
          color={color}
          variant="outlined"
          label={getLabel(item)}
          sx={{ fontSize: "0.7rem", height: 22 }}
        />
      ))}
      {items.length > MAX_CHIPS && (
        <Chip size="small" label={`외 ${items.length - MAX_CHIPS}건`} sx={{ fontSize: "0.7rem", height: 22 }} />
      )}
    </Stack>
  );
}

function DateImpactPreview({ date, change }) {
  const { darkMode } = useDarkMode();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setResult(null);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await api.post("/preview/change", { date, change });
        setResult(res.data);
      } catch (err) {
        console.error("미리보기 조회 실패:", err);
        setResult(null);
        setError(true);
      } finally {
        setLoading(false);
      }
    }, 500); // 디바운스
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, JSON.stringify(change)]);

  if (!date) return null;

  if (loading && !result) {
    return (
      <Box display="flex" alignItems="center" gap={1} py={1}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">영향 확인 중...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", py: 1 }}>
        미리보기를 불러오지 못했습니다.
      </Typography>
    );
  }

  if (!result) return null;

  const { state, impacts } = result;
  const invalidatedLessons = impacts?.invalidatedLessons || [];
  const invalidatedPractices = impacts?.invalidatedPractices || [];
  const notes = impacts?.notes || [];
  const hasChange = !!change?.kind;
  const hasImpact = invalidatedLessons.length > 0 || invalidatedPractices.length > 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mt: 1.5,
        bgcolor: hasChange
          ? (hasImpact
            ? (darkMode ? "rgba(255, 152, 0, 0.10)" : "#fff8f0")
            : (darkMode ? "rgba(76, 175, 80, 0.10)" : "#f4faf0"))
          : (darkMode ? "grey.900" : "#fafafa")
      }}
    >
      {/* 날짜 유효 상태 요약 */}
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center">
        <Typography variant="subtitle2" fontWeight={600} sx={{ mr: 0.5 }}>
          {state.date} ({state.dayLabel})
        </Typography>
        {state.holiday && (
          <Chip icon={<EventBusy />} size="small" color="error" variant="outlined"
            label={`휴일: ${state.holiday.reason || "휴일"}`} sx={{ fontSize: "0.7rem", height: 22 }} />
        )}
        <Chip size="small" variant="outlined"
          color={state.lesson.isOpen ? "default" : "error"}
          label={state.lesson.isOpen
            ? `레슨 ${state.lesson.startTime || "-"}~${state.lesson.endTime || "-"}${state.lesson.isOverride ? " (예외)" : ""}`
            : "레슨 불가 (예외)"}
          sx={{ fontSize: "0.7rem", height: 22 }} />
        <Chip size="small" variant="outlined"
          color={state.practice.isOpen ? "default" : "error"}
          label={state.practice.isOpen
            ? `연습실 ${state.practice.startTime || "-"}~${state.practice.endTime || "-"}${state.practice.isOverride ? " (예외)" : ""}`
            : "연습실 휴무"}
          sx={{ fontSize: "0.7rem", height: 22 }} />
        <Chip size="small" variant="outlined"
          label={`근무 선생님 ${state.workingTeacherCount}명`}
          sx={{ fontSize: "0.7rem", height: 22 }} />
      </Stack>

      {/* 예약 현황 */}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
        예약 현황: 레슨 {state.reservations.lessonCount}건
        {state.reservations.blockCount > 0 && ` · 블록 ${state.reservations.blockCount}건`}
        {` · 연습실 직접 예약 ${state.reservations.manualPracticeCount}건`}
        {state.lesson3LessonSlots?.length > 0 && ` · Lesson 3 레슨실 전환 슬롯 ${state.lesson3LessonSlots.length}개`}
      </Typography>

      {state.teachers?.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          근무: {state.teachers.map(t => `${t.name} ${t.windows.join(", ")}`).join(" / ")}
        </Typography>
      )}

      {/* 가상 변경 영향 */}
      {hasChange && (
        <>
          <Divider sx={{ my: 1 }} />
          {hasImpact ? (
            <Box>
              {invalidatedLessons.length > 0 && (
                <Box mb={invalidatedPractices.length > 0 ? 1 : 0}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <WarningIcon color="warning" sx={{ fontSize: 16 }} />
                    <Typography variant="caption" fontWeight={600} color="warning.main">
                      영향받는 레슨 {invalidatedLessons.length}건
                    </Typography>
                  </Stack>
                  <ChipList
                    items={invalidatedLessons}
                    color="warning"
                    getLabel={(l) => `${l.memberName}${l.isBlock ? " (블록)" : ""} ${l.time}`}
                  />
                </Box>
              )}
              {invalidatedPractices.length > 0 && (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <WarningIcon color="warning" sx={{ fontSize: 16 }} />
                    <Typography variant="caption" fontWeight={600} color="warning.main">
                      영향받는 연습실 예약 {invalidatedPractices.length}건
                    </Typography>
                  </Stack>
                  <ChipList
                    items={invalidatedPractices}
                    color="warning"
                    getLabel={(p) => `${p.memberName} ${p.roomName} ${p.time}`}
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <CheckCircle color="success" sx={{ fontSize: 16 }} />
              <Typography variant="caption" fontWeight={600} color="success.main">
                이 변경으로 영향받는 예약이 없습니다.
              </Typography>
            </Stack>
          )}

          {impacts?.refundTargets && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              티켓 반환 선택 시 환불 대상: {impacts.refundTargets.length}명
            </Typography>
          )}

          {notes.map((note, idx) => (
            <Stack key={idx} direction="row" alignItems="flex-start" spacing={0.5} mt={0.5}>
              <InfoIcon color="info" sx={{ fontSize: 14, mt: "2px" }} />
              <Typography variant="caption" color="text.secondary">{note}</Typography>
            </Stack>
          ))}
        </>
      )}
    </Paper>
  );
}

export default DateImpactPreview;
